import { createContext } from "react";
import { createContextualCan } from "@casl/react";
import {
  AbilityBuilder,
  createMongoAbility,
  PureAbility,
  MongoQuery,
} from "@casl/ability";
import { User } from "oidc-client-ts";
import { EAppModule } from "dto/EAppModule";
import { EPrivileges } from "dto/EPrivileges";
import { APP_FUNCTION_MODULE, EAppFunction } from "dto/EAppFunction";

export const AbilityContext = createContext<AppAbility>(null);
export const Can = createContextualCan(AbilityContext.Consumer);

type CrudActions = "accessModule";
type Ability =
  /** Funkcja aplikacji z /ui/privileges (Functions) — pozycje menu i ich podpozycje. */
  | ["view", EAppFunction]
  | [CrudActions, "incoming"]
  | [CrudActions, "outgoing"]
  | [CrudActions, "addressBook"]
  | [CrudActions, "desk"]
  | [CrudActions, "cases"]
  | [CrudActions, "casesext"]
  | [CrudActions, "drive"]
  | [CrudActions, "casedrive"]
  | [CrudActions, "tasks"]
  | [CrudActions, "notes"]
  | [CrudActions, "email"]
  | [CrudActions, "letters"]
  | [CrudActions, "courtportal"]
  | [CrudActions, "word"]
  | [CrudActions, "wordext"]
  | [CrudActions, "encrypted_parcels"]
  | [CrudActions, "workspace"]
  | [CrudActions, "mailroom"]
  | [CrudActions, "admin"]
  | [CrudActions, EPrivileges];

export type AppAbility = PureAbility<Ability, MongoQuery>;

/**
 * `companyPrivileges` to `CompanyContext.privileges` (docs/model-kluczy-firmowych.md, F1):
 * 15 wartości EPrivileges wyliczonych z roli zalogowanego W FIRMIE. Domyślnie pusta tablica,
 * żeby dwuargumentowe wywołania (goście, ekrany bez zamontowanego CompanyProvider) dalej
 * działały — tam liczą się WYŁĄCZNIE moduły, jak dotąd.
 *
 * F8 (ta faza): dotąd wołane wyłącznie z dwoma argumentami — bo liczone w ciele App(),
 * ZANIM CompanyProvider zdążył się zamontować (jest jego potomkiem w drzewie JSX) — więc
 * żaden z 15 uprawnień firmowych nie nadawał ANI JEDNEJ zdolności CASL. Naprawia to
 * components/CompanyProvider/CompanyAbilityProvider w App.tsx, który liczy ability jako
 * POTOMEK CompanyProvider i przekazuje tu trzeci argument.
 */
export function defineAbilityFor(
  user: User,
  modules: string[],
  companyPrivileges: string[] = [],
  functions?: string[] | null,
) {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  // Funkcje aplikacji (dto/EAppFunction) — scalenie pakietu z metryką pracownika liczone
  // w BE (Access.AppFunctions, odpowiednik NavigationItem z Magistratu):
  //   undefined — /ui/privileges jeszcze nie wróciło: nic nie pokazujemy, żeby pozycje
  //               zastrzeżone rolą nie mignęły osobie bez uprawnień;
  //   null      — BE ich nie podał (błąd żądania albo starszy BE): nie ograniczamy ról,
  //               tylko pakiet, jak przed wprowadzeniem funkcji;
  //   lista     — dokładnie to, co wyliczył BE.
  if (functions) {
    for (const fn of functions) {
      can("view", fn as EAppFunction);
    }
  } else if (functions === null) {
    for (const fn of Object.values(EAppFunction)) {
      const module = APP_FUNCTION_MODULE[fn];
      if (!module || modules.includes(module)) {
        can("view", fn);
      }
    }
  }

  if (modules.includes(EAppModule.Incoming)) {
    can("accessModule", "incoming");
  }

  if (modules.includes(EAppModule.Outgoing)) {
    can("accessModule", "outgoing");
  }

  if (modules.includes(EAppModule.AddressBook)) {
    can("accessModule", "addressBook");
  }

  if (modules.includes(EAppModule.Desk)) {
    can("accessModule", "desk");
  }

  if (modules.includes(EAppModule.Cases)) {
    can("accessModule", "cases");
  }

  if (modules.includes(EAppModule.CasesExt)) {
    can("accessModule", "casesext");
  }

  if (modules.includes(EAppModule.Drive)) {
    can("accessModule", "drive");
  }

  if (modules.includes(EAppModule.CaseDrive)) {
    can("accessModule", "casedrive");
  }

  if (modules.includes(EAppModule.Tasks)) {
    can("accessModule", "tasks");
  }

  if (modules.includes(EAppModule.Notes)) {
    can("accessModule", "notes");
  }

  if (modules.includes(EAppModule.Email)) {
    can("accessModule", "email");
  }

  if (modules.includes(EAppModule.Letters)) {
    can("accessModule", "letters");
  }

  if (modules.includes(EAppModule.Word)) {
    can("accessModule", "word");
  }

  if (modules.includes(EAppModule.WordExt)) {
    can("accessModule", "wordext");
  }

  if (modules.includes(EAppModule.EncryptedParcels)) {
    can("accessModule", "encrypted_parcels");
  }

  if (modules.includes(EAppModule.Workspace)) {
    can("accessModule", "workspace");
  }

  if (modules.includes(EAppModule.CourtPortal)) {
    can("accessModule", "courtportal");
  }

  if (modules.includes(EAppModule.Mailroom)) {
    can("accessModule", "mailroom");
  }

  if (user) {
    const role = user.profile["role"];

    if (role && role === "admin") {
      can("accessModule", "admin");
    }
  }

  // Uprawnienia FIRMOWE (EPrivileges) — patrz komentarz nad `companyPrivileges` wyżej.
  // BE może zwrócić wartość, której ta wersja FE nie zna (nowe uprawnienie dodane po
  // wydaniu buildu) — `can()` z nieznaną wartością po prostu nie odblokowuje niczego
  // istniejącego w UI, więc filtrować do `EPrivileges` nie trzeba.
  for (const privilege of companyPrivileges) {
    can("accessModule", privilege as EPrivileges);
  }

  return build();
}
